/**
 * OCPP-PNC — ISO 15118 Plug & Charge tester panel (v2.0.1-only).
 *
 * Lets a QA tester drive the three CP→CSMS PnC exchanges with real artifacts
 * (eMAID, contract-cert PEM or OCSP hash data, EXI blobs) and read the real
 * CSMS verdict. The simulator relays verbatim — it does not validate certs
 * itself; that's the CSMS's job, which is exactly what's under test.
 */
import { useState } from 'react';
import { usePlugAndChargeStore, type OcspRequestData } from '@/state/usePlugAndChargeStore';

const HASH_ALGORITHMS = ['SHA256', 'SHA384', 'SHA512'] as const;

function field(label: string, node: JSX.Element): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-slate-400">{label}</span>
      {node}
    </label>
  );
}

const inputCls =
  'rounded border border-slate-600 bg-slate-950 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base';
const btnCls =
  'rounded bg-primary-hover px-3 py-1 text-xs text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40';

export function PlugAndChargePanel(): JSX.Element {
  const status = usePlugAndChargeStore((s) => s.status);
  const error = usePlugAndChargeStore((s) => s.error);
  const authResult = usePlugAndChargeStore((s) => s.authResult);
  const certResult = usePlugAndChargeStore((s) => s.certResult);
  const ocspResult = usePlugAndChargeStore((s) => s.ocspResult);
  const authorizePnC = usePlugAndChargeStore((s) => s.authorizePnC);
  const get15118 = usePlugAndChargeStore((s) => s.get15118Certificate);
  const getCertStatus = usePlugAndChargeStore((s) => s.getCertificateStatus);

  // Collapsed by default — most QA flows don't touch PnC and the panel is tall.
  const [open, setOpen] = useState(false);

  // eMAID authorize
  const [eMAID, setEMAID] = useState('DE-ABC-C12345678-9');
  const [certPem, setCertPem] = useState('');
  const [useHashData, setUseHashData] = useState(false);

  // shared OCSP hash data (used by both the authorize hashData option and the
  // GetCertificateStatus exchange)
  const [ocsp, setOcsp] = useState<OcspRequestData>({
    hashAlgorithm: 'SHA256',
    issuerNameHash: '',
    issuerKeyHash: '',
    serialNumber: '',
    responderURL: 'https://csms.example/ocsp',
  });
  const setOcspField = (k: keyof OcspRequestData, v: string): void =>
    setOcsp((p) => ({ ...p, [k]: v }));

  // Get15118EVCertificate
  const [schemaVersion, setSchemaVersion] = useState('urn:iso:15118:2:2013:MsgDef');
  const [exiAction, setExiAction] = useState<'Install' | 'Update'>('Install');
  const [exiRequest, setExiRequest] = useState('');

  const pending = status === 'pending';

  const onAuthorize = (): void => {
    void authorizePnC({
      eMAID,
      ...(certPem.trim() !== '' ? { certificate: certPem } : {}),
      ...(useHashData ? { iso15118CertificateHashData: [ocsp] } : {}),
    });
  };

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-3"
      data-testid="pnc-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="pnc-body"
        className="flex w-full items-center justify-between gap-2 text-left focus:outline-none focus:ring-2 focus:ring-primary-base"
        data-testid="pnc-toggle"
      >
        <span className="flex items-center gap-1.5">
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`h-3 w-3 fill-current text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          >
            <path d="M5.5 3.5L10.5 8l-5 4.5z" />
          </svg>
          <span className="text-sm font-medium text-slate-100">Plug &amp; Charge (ISO 15118)</span>
        </span>
        <span className="text-[10px] text-slate-500">v2.0.1 · {open ? 'hide' : 'expand'}</span>
      </button>

      {open ? (
        <div id="pnc-body" className="mt-3">
          <p className="mb-3 text-[11px] text-slate-500">
            Supply real artifacts; the simulator sends them verbatim and shows the CSMS&apos;s verdict.
          </p>

          {error !== null ? (
        <p className="mb-3 rounded bg-rose-950/60 px-2 py-1 text-[11px] text-rose-300" data-testid="pnc-error">
          {error}
        </p>
      ) : null}

      {/* ── eMAID Authorize ─────────────────────────────────────────── */}
      <fieldset className="mb-3 rounded border border-slate-700 p-2">
        <legend className="px-1 text-[11px] text-slate-400">eMAID Authorize</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {field(
            'eMAID',
            <input
              type="text"
              value={eMAID}
              onChange={(e) => setEMAID(e.target.value)}
              className={`${inputCls} font-mono`}
              data-testid="pnc-emaid"
            />,
          )}
          <label className="flex items-end gap-1 text-[11px] text-slate-400">
            <input
              type="checkbox"
              checked={useHashData}
              onChange={(e) => setUseHashData(e.target.checked)}
              data-testid="pnc-use-hashdata"
            />
            include iso15118CertificateHashData
          </label>
        </div>
        {field(
          'Contract certificate (PEM, optional)',
          <textarea
            value={certPem}
            onChange={(e) => setCertPem(e.target.value)}
            rows={2}
            placeholder="-----BEGIN CERTIFICATE-----…"
            className={`${inputCls} mt-1 font-mono`}
            data-testid="pnc-cert-pem"
          />,
        )}
        <button type="button" onClick={onAuthorize} disabled={pending} className={`${btnCls} mt-2`} data-testid="pnc-authorize">
          Authorize (PnC)
        </button>
        {authResult !== null ? (
          <p className="mt-2 text-[11px] text-slate-300" data-testid="pnc-auth-result">
            status: <span className="font-mono text-slate-100">{authResult.authorizationStatus}</span>
            {authResult.certificateStatus !== undefined ? (
              <>
                {' · '}certificateStatus:{' '}
                <span className="font-mono text-slate-100">{authResult.certificateStatus}</span>
              </>
            ) : null}
          </p>
        ) : null}
      </fieldset>

      {/* ── Get15118EVCertificate (EXI) ─────────────────────────────── */}
      <fieldset className="mb-3 rounded border border-slate-700 p-2">
        <legend className="px-1 text-[11px] text-slate-400">Get15118EVCertificate (EXI)</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {field(
            'iso15118SchemaVersion',
            <input
              type="text"
              value={schemaVersion}
              onChange={(e) => setSchemaVersion(e.target.value)}
              className={`${inputCls} font-mono`}
              data-testid="pnc-exi-version"
            />,
          )}
          {field(
            'action',
            <select
              value={exiAction}
              onChange={(e) => setExiAction(e.target.value as 'Install' | 'Update')}
              className={inputCls}
              data-testid="pnc-exi-action"
            >
              <option value="Install">Install</option>
              <option value="Update">Update</option>
            </select>,
          )}
        </div>
        {field(
          'exiRequest (base64)',
          <textarea
            value={exiRequest}
            onChange={(e) => setExiRequest(e.target.value)}
            rows={2}
            className={`${inputCls} mt-1 font-mono`}
            data-testid="pnc-exi-request"
          />,
        )}
        <button
          type="button"
          onClick={() => void get15118({ iso15118SchemaVersion: schemaVersion, action: exiAction, exiRequest })}
          disabled={pending || exiRequest.trim() === ''}
          className={`${btnCls} mt-2`}
          data-testid="pnc-get15118"
        >
          Get 15118 certificate
        </button>
        {certResult !== null ? (
          <p className="mt-2 break-all text-[11px] text-slate-300" data-testid="pnc-15118-result">
            status: <span className="font-mono text-slate-100">{certResult.status}</span> · exiResponse:{' '}
            <span className="font-mono text-slate-100">{certResult.exiResponse || '—'}</span>
          </p>
        ) : null}
      </fieldset>

      {/* ── GetCertificateStatus (OCSP) ─────────────────────────────── */}
      <fieldset className="rounded border border-slate-700 p-2">
        <legend className="px-1 text-[11px] text-slate-400">GetCertificateStatus (OCSP)</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {field(
            'hashAlgorithm',
            <select
              value={ocsp.hashAlgorithm}
              onChange={(e) => setOcspField('hashAlgorithm', e.target.value)}
              className={inputCls}
              data-testid="pnc-ocsp-alg"
            >
              {HASH_ALGORITHMS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>,
          )}
          {field('serialNumber', <input type="text" value={ocsp.serialNumber} onChange={(e) => setOcspField('serialNumber', e.target.value)} className={`${inputCls} font-mono`} data-testid="pnc-ocsp-serial" />)}
          {field('issuerNameHash', <input type="text" value={ocsp.issuerNameHash} onChange={(e) => setOcspField('issuerNameHash', e.target.value)} className={`${inputCls} font-mono`} data-testid="pnc-ocsp-issuer-name" />)}
          {field('issuerKeyHash', <input type="text" value={ocsp.issuerKeyHash} onChange={(e) => setOcspField('issuerKeyHash', e.target.value)} className={`${inputCls} font-mono`} data-testid="pnc-ocsp-issuer-key" />)}
        </div>
        {field('responderURL', <input type="text" value={ocsp.responderURL} onChange={(e) => setOcspField('responderURL', e.target.value)} className={`${inputCls} mt-1 font-mono`} data-testid="pnc-ocsp-responder" />)}
        <button
          type="button"
          onClick={() => void getCertStatus({ ocspRequestData: ocsp })}
          disabled={pending || ocsp.issuerNameHash.trim() === '' || ocsp.serialNumber.trim() === ''}
          className={`${btnCls} mt-2`}
          data-testid="pnc-certstatus"
        >
          Check certificate status
        </button>
        {ocspResult !== null ? (
          <p className="mt-2 break-all text-[11px] text-slate-300" data-testid="pnc-ocsp-result">
            status: <span className="font-mono text-slate-100">{ocspResult.status}</span> · ocspResult:{' '}
            <span className="font-mono text-slate-100">{ocspResult.ocspResult ?? '—'}</span>
          </p>
        ) : null}
          </fieldset>
        </div>
      ) : null}
    </section>
  );
}
